import { api } from './api';
import type { Deelnemer, CreateDeelnemerRequest, DeelnemerAntwoordDto, Antwoord, Sessie } from '@types';
import type { SubmitAntwoordenRequest } from '@types';

// ============================================
// Deelnemer API Functions
// ============================================

/**
 * Maak een nieuwe deelnemer aan voor een sessie
 * Gebruikt voor deelnemers om zichzelf toe te voegen aan een sessie
 */
export const createDeelnemer = async (request: CreateDeelnemerRequest): Promise<Deelnemer> => {
  const response = await api.post<Deelnemer>('/deelnemers', request);
  return response.data;
};

/**
 * Haal deelnemer op door token
 * Gebruikt voor deelnemers om hun eigen gegevens te herstellen
 */
export const getDeelnemerByToken = async (token: string): Promise<Deelnemer> => {
  const response = await api.get<Deelnemer>(`/deelnemers/token/${token}`);
  return response.data;
};

/**
 * Haal deelnemers op voor een sessie via sessiecode
 * @param sessieCode - De sessie code
 * @returns Lijst met deelnemers
 */
export const getDeelnemersBySessieCode = async (sessieCode: string): Promise<Deelnemer[]> => {
  const response = await api.get<Deelnemer[]>(`/sessies/deelnemers/${sessieCode}`);
  return response.data;
};

/**
 * Haal een sessie op via deelnemers endpoint (zonder authenticatie)
 * Gebruikt voor deelnemers om sessie-informatie te tonen bij deelname
 * @param sessieCode - De sessie code
 * @returns De sessie of null als niet gevonden
 */
export const getSessieBySessieCodePublic = async (sessieCode: string): Promise<Sessie | null> => {
  const response = await api.get<Sessie>(`/deelnemers/sessie/${sessieCode}`);
  return response.data || null;
};

// ============================================
// Antwoord Functions (gerelateerd aan deelnemers)
// ============================================

/**
 * Verzend antwoorden voor een deelnemer
 * @param deelnemerToken - De token van de deelnemer
 * @param axisId - Het ID van de axis
 * @param antwoorden - Lijst met antwoorden (stellingId, score, toelichting)
 * @returns Lijst met opgeslagen antwoorden
 */
export const submitAntwoorden = async (deelnemerToken: string, axisId: number, antwoorden: DeelnemerAntwoordDto[]): Promise<Antwoord[]> => {
  const request: SubmitAntwoordenRequest = { axisId, antwoorden };
  const response = await api.post<Antwoord[]>('/antwoorden', request, {
    headers: {
      'X-Deelnemer-Token': deelnemerToken
    }
  });
  return response.data;
};

/**
 * Haal antwoorden op voor een deelnemer door token
 * @param deelnemerToken - De token van de deelnemer
 * @returns Array van deelnemer antwoorden
 */
export const getAntwoordenByDeelnemerToken = async (deelnemerToken: string): Promise<DeelnemerAntwoordDto[]> => {
  const response = await api.get<DeelnemerAntwoordDto[]>(`/antwoorden/deelnemer/${deelnemerToken}`, {
    headers: {
      'X-Deelnemer-Token': deelnemerToken
    }
  });
  return response.data;
};
